#!/usr/bin/env node

/**
 * TestDino CLI Entry Point
 * Main entry point for the tdpw CLI tool
 */

import { commandManager } from './commands';
import {
  BaseError,
  ConfigurationError,
  ValidationError,
  AuthenticationError,
  NetworkError,
  FileSystemError,
  UsageLimitError,
  ExitCode,
} from '@/types';
import { logger, setVerbose } from '@/utils/logger';

/**
 * CLI Application main class
 */
class TestDinoCLI {
  /**
   * Main application entry point
   */
  async run(args: string[] = process.argv): Promise<void> {
    try {
      // Handle help and version flags manually for better control
      if (this.shouldShowHelp(args)) {
        const helpResult = this.handleHelpCommand(args);
        if (helpResult.handled) {
          if (helpResult.showCache) {
            commandManager.showCacheHelp();
          } else if (helpResult.showLastFailed) {
            commandManager.showLastFailedHelp();
          } else if (helpResult.showUpload) {
            commandManager.showUploadHelp();
          } else {
            commandManager.showHelp();
          }
          process.exit(ExitCode.SUCCESS);
        }
      }

      if (this.shouldShowVersion(args)) {
        logger.info(`tdpw v${commandManager.getVersion()}`);
        process.exit(ExitCode.SUCCESS);
      }

      // Check for legacy syntax and transform to new format
      const transformedArgs = this.handleLegacySyntax(args);

      // Execute commands through the command manager
      await commandManager.parseAndExecute(transformedArgs);

      process.exit(ExitCode.SUCCESS);
    } catch (error) {
      await this.handleError(error);
    }
  }

  /**
   * Check if help should be displayed
   */
  private shouldShowHelp(args: string[]): boolean {
    return (
      args.includes('--help') || args.includes('-h') || args.includes('help')
    );
  }

  /**
   * Handle help command and determine which help to show
   */
  private handleHelpCommand(args: string[]): {
    handled: boolean;
    showCache: boolean;
    showLastFailed: boolean;
    showUpload: boolean;
  } {
    const userArgs = args.slice(2); // Remove node and script path

    // Check for subcommand help: "cache --help", "last-failed --help", "upload --help"
    if (userArgs.length >= 2) {
      if (
        userArgs[0] === 'cache' &&
        (userArgs[1] === '--help' || userArgs[1] === '-h')
      ) {
        return {
          handled: true,
          showCache: true,
          showLastFailed: false,
          showUpload: false,
        };
      }
      if (
        userArgs[0] === 'last-failed' &&
        (userArgs[1] === '--help' || userArgs[1] === '-h')
      ) {
        return {
          handled: true,
          showCache: false,
          showLastFailed: true,
          showUpload: false,
        };
      }
      if (
        userArgs[0] === 'upload' &&
        (userArgs[1] === '--help' || userArgs[1] === '-h')
      ) {
        return {
          handled: true,
          showCache: false,
          showLastFailed: false,
          showUpload: true,
        };
      }
    }

    // Check for "help cache", "help last-failed", or "help upload"
    if (userArgs.length >= 2 && userArgs[0] === 'help') {
      if (userArgs[1] === 'cache') {
        return {
          handled: true,
          showCache: true,
          showLastFailed: false,
          showUpload: false,
        };
      }
      if (userArgs[1] === 'last-failed') {
        return {
          handled: true,
          showCache: false,
          showLastFailed: true,
          showUpload: false,
        };
      }
      if (userArgs[1] === 'upload') {
        return {
          handled: true,
          showCache: false,
          showLastFailed: false,
          showUpload: true,
        };
      }
    }

    // Check for main help: "--help", "-h", or just "help"
    if (
      userArgs.length === 0 ||
      (userArgs.length === 1 &&
        (userArgs[0] === '--help' ||
          userArgs[0] === '-h' ||
          userArgs[0] === 'help'))
    ) {
      return {
        handled: true,
        showCache: false,
        showLastFailed: false,
        showUpload: false,
      };
    }

    return {
      handled: false,
      showCache: false,
      showLastFailed: false,
      showUpload: false,
    };
  }

  /**
   * Check if version should be displayed
   */
  private shouldShowVersion(args: string[]): boolean {
    return args.includes('--version');
  }

  /**
   * Handle legacy syntax for backward compatibility
   * Transforms: npx tdpw ./path --flags
   * To:         npx tdpw upload ./path --flags
   */
  private handleLegacySyntax(args: string[]): string[] {
    const userArgs = args.slice(2); // Remove node and script path

    // If no args, return as-is
    if (userArgs.length === 0) {
      return args;
    }

    const firstArg = userArgs[0];

    // Known subcommands - not legacy syntax
    const knownCommands = ['cache', 'last-failed', 'upload', 'help'];

    // If first arg is a known command, return as-is
    if (firstArg && knownCommands.includes(firstArg)) {
      return args;
    }

    // If first arg is a flag, return as-is
    if (firstArg?.startsWith('-')) {
      return args;
    }

    // If we reach here, first arg is likely a path (legacy syntax)
    // Check if there are any upload-related flags
    const uploadFlags = [
      '--upload-images',
      '--upload-videos',
      '--upload-html',
      '--upload-traces',
      '--upload-files',
      '--upload-full-json',
      '--json-report',
      '--html-report',
      '--trace-dir',
    ];

    const hasUploadFlag = userArgs.some(arg => uploadFlags.includes(arg));

    // Legacy syntax detected: path provided without 'upload' command
    if (hasUploadFlag || firstArg) {
      logger.warn('DEPRECATION WARNING: Legacy syntax detected.');
      logger.info('Old: npx tdpw ./path --flags');
      logger.info('New: npx tdpw upload ./path --flags');
      logger.info('Legacy syntax will be removed in a future version.');
      logger.blank();

      // Transform: insert 'upload' command after script name
      const nodePath = args[0] || 'node';
      const scriptPath = args[1] || 'tdpw';
      return [nodePath, scriptPath, 'upload', ...userArgs];
    }

    return args;
  }

  /**
   * Professional error handling with appropriate messaging
   */
  private async handleError(error: unknown): Promise<void> {
    const isJsonMode = process.argv.includes('--json');
    const isVerbose =
      process.argv.includes('--verbose') || process.argv.includes('-v');

    setVerbose(isVerbose);

    // In JSON mode, emit structured error to stdout and exit
    if (isJsonMode) {
      const errorCode =
        error instanceof BaseError ? error.code : 'GENERAL_ERROR';
      const message = error instanceof Error ? error.message : 'Unknown error';

      const jsonOutput = {
        success: false as const,
        error: { code: errorCode, message },
      };

      process.stdout.write(JSON.stringify(jsonOutput) + '\n');

      if (error instanceof BaseError) {
        process.exit(this.getExitCode(error));
      }
      process.exit(ExitCode.GENERAL_ERROR);
    }

    if (error instanceof BaseError) {
      logger.error(error.message);

      // Show helpful suggestions without being verbose
      switch (true) {
        case error instanceof ConfigurationError:
          logger.info('Check your API token and configuration');
          break;
        case error instanceof ValidationError:
          logger.info('Use --help for correct usage');
          break;
        case error instanceof AuthenticationError:
          logger.info('Verify your API token has proper permissions');
          break;
        case error instanceof NetworkError:
          logger.info('Check your internet connection');
          break;
        case error instanceof FileSystemError:
          logger.info('Verify the specified paths exist and are readable');
          break;
      }

      // Show stack trace only in verbose mode
      if (isVerbose && error.stack) {
        logger.section('Stack trace:');
        logger.verbose(error.stack);
      }

      process.exit(this.getExitCode(error));
    } else if (error instanceof Error) {
      logger.error(error.message);

      if (!isVerbose) {
        logger.info('Use --verbose for detailed error information');
      } else if (error.stack) {
        logger.section('Stack trace:');
        logger.verbose(error.stack);
      }

      process.exit(ExitCode.GENERAL_ERROR);
    } else {
      logger.error('An unexpected error occurred');
      if (isVerbose) {
        logger.debug(`Error details: ${error}`);
      }
      process.exit(ExitCode.GENERAL_ERROR);
    }
  }

  /**
   * Get appropriate exit code for error type
   */
  private getExitCode(error: BaseError): number {
    if (error instanceof AuthenticationError)
      return ExitCode.AUTHENTICATION_ERROR;
    if (error instanceof NetworkError) return ExitCode.NETWORK_ERROR;
    if (error instanceof FileSystemError) return ExitCode.FILE_NOT_FOUND_ERROR;
    if (error instanceof UsageLimitError) return ExitCode.USAGE_LIMIT_ERROR;
    return ExitCode.GENERAL_ERROR;
  }
}

/**
 * Application execution
 */
async function main(): Promise<void> {
  const cli = new TestDinoCLI();
  await cli.run();
}

// Execute CLI when this module is run
(async () => {
  try {
    await main();
  } catch (error) {
    logger.error(`Fatal error during CLI execution: ${error}`);
    process.exit(ExitCode.GENERAL_ERROR);
  }
})();

// Export for testing
export { TestDinoCLI, main };
